import { NavigationError } from "@angular/router"
import type { Event as BugsnagEvent } from "@bugsnag/js"

// A lazy-loaded route/module fetch that didn't complete. Webpack raises ChunkLoadError
// ("Loading chunk N failed"), esbuild a TypeError ("Failed to fetch dynamically imported module").
// Deploys keep old hashed assets, so the file exists — the fetch was interrupted or blocked.
export function isLazyLoadError (err: any): boolean {
  const message = err?.message ?? String(err ?? '')
  return err?.name === 'ChunkLoadError' ||
    /Loading chunk .* failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message) ||
    /error loading dynamically imported module/i.test(message) // Firefox wording
}

// Webpack gives up on a chunk after 120s: a very slow connection, not a missing file.
export function isLazyLoadTimeout (err: any): boolean {
  return isLazyLoadError(err) && /\(timeout:/i.test(err?.message ?? '')
}

// For Bugsnag.start({ onError }). Drops lazy-load failures that are just connectivity:
// offline, or the webpack timeout. Runs before Angular bootstraps, hence navigator.onLine.
export function reportLazyLoadError (event: BugsnagEvent): boolean {
  const err = event.originalError
  if (!isLazyLoadError(err)) { return true }
  if (isLazyLoadTimeout(err)) { return false }
  return navigator.onLine
}

const RELOAD_KEY = 'influx.lazyLoadRetry'
const RELOAD_WINDOW_MS = 30_000

// For provideRouter(..., withNavigationErrorHandler(lazyLoadRetryHandler)). Retries a failed
// lazy load by reloading onto the target URL, at most once per URL per 30s so a genuinely
// broken chunk can't loop. The URL is resolved against <base href> like PathLocationStrategy does.
// `reload` is injectable only so the guard can be unit tested — jsdom's window.location is unforgeable.
export function createLazyLoadRetryHandler (reload: (href: string) => void = (href) => window.location.assign(href)) {
  return (e: NavigationError) => {
    if (!isLazyLoadError(e.error)) { return }

    const now = Date.now()
    let last: string | null = null
    try { last = sessionStorage.getItem(RELOAD_KEY) } catch { /* storage unavailable */ }
    if (last) {
      const [url, at] = last.split('|')
      if (url === e.url && now - Number(at) < RELOAD_WINDOW_MS) { return }
    }
    try { sessionStorage.setItem(RELOAD_KEY, `${e.url}|${now}`) } catch { /* storage unavailable */ }

    reload(new URL(e.url.replace(/^\//, ''), document.baseURI).href)
  }
}

export const lazyLoadRetryHandler = createLazyLoadRetryHandler()
